// The Flow engine tour — the full walkthrough of the execution half of the
// cortex split: the embedded Flow studio (/flow/*), its sections (endpoints,
// node services, folders, history, export, environment, project knowledge)
// and how cortex drives the engine. Available to EVERY project (each cortex
// project has its own paired engine workspace).
//
// The studio lives under /flow/projects/<forkId>/…, and the fork id is only
// known at runtime — so intra-studio steps compute their destination from the
// CURRENT path (the `page` function form; see TutorialFlow.jsx). Opening
// "flow" first makes the studio resolve + redirect to the paired project's
// endpoints, after which the fork id is in the URL.

// Absolute path of a studio section, derived from the current location. Before
// the studio has resolved its project (no /flow/projects/<id> in the path yet)
// we stay put — the step's fallback target keeps the tour coherent.
const studioSection = (section) => (path) => {
  const m = String(path || "").match(/^(.*\/flow\/projects\/[^/]+)/);
  return m ? `${m[1]}/${section}` : null;
};

// Content region of the studio (below the homebar); generous fallbacks.
const STUDIO_CONTENT = [
  ".flow-studio-root .flex-1.min-h-0.overflow-hidden",
  ".flow-studio-root",
  "main",
];
const HOMEBAR = ".flow-studio-homebar";
const homebarLink = (section) => [
  `${HOMEBAR} a[href*="/${section}"]`,
  HOMEBAR,
  "main",
];

export const flowTour = {
  key: "flow-engine",
  title: "The Flow engine",
  description:
    "The execution half of Cortex: the paired engine workspace — endpoints (flow graphs), node services, environment, history, the project knowledge vault, and how cortex dispatches pipelines to it.",
  steps: [
    {
      title: "Cortex is two systems",
      body:
        "Everything you author in Cortex — elements, sources, mappings, rules, pipelines — is the " +
        "<b>recipe</b>. The recipe doesn't move data by itself: a paired <b>Flow engine project</b> " +
        "executes it. Every Cortex project gets its <b>own engine workspace</b>, cloned from a " +
        "common template (<code>core</code>), so fixes to the template reach every project while " +
        "each project can still have bespoke endpoints (like a demo's dashboard).<br/><br/>This " +
        "tour walks the whole engine surface.",
    },
    {
      target: '[data-tour="nav-flow"]',
      placement: "right",
      title: "Flow — the engine studio, embedded",
      body:
        "The <b>Flow</b> item opens the engine's own studio inside Cortex. It opens on the engine " +
        "project you used last — the picker at the studio's top right switches between them (each " +
        "Cortex project pairs with its own; <code>core</code> is the shared template). Day-to-day " +
        "governance never requires coming here — but everything the engine does is inspectable and " +
        "editable.",
    },
    {
      page: "flow",
      target: [HOMEBAR, "main"],
      waitMs: 25000,
      title: "The studio shell",
      body:
        "The studio's own navigation: <b>Endpoints</b>, <b>Node Services</b>, <b>Folders</b>, " +
        "<b>History</b>, <b>Export</b>, <b>Environment</b> and <b>Project</b>, with search and a " +
        "project picker on the right. The next steps visit each section.",
    },
    {
      page: studioSection("endpoints"),
      target: STUDIO_CONTENT,
      waitMs: 15000,
      title: "Endpoints — the flows",
      body:
        "An <b>endpoint</b> is one callable flow: a <b>graph of nodes</b> (functions, service " +
        "calls, branches) wired by edges, editable on a canvas with live test runs. Everything the " +
        "engine does for Cortex is an endpoint with a <b>stable name</b>: " +
        "<code>cortex.source.discover_schema</code>, <code>cortex.pipeline.run</code>, " +
        "<code>cortex.strategy.&lt;kind&gt;.apply</code> (one per transformation kind), " +
        "<code>cortex.analytics.*</code>, and the <code>*.view</code> endpoints that serve this " +
        "project's Apps. Open one to see its canvas.",
    },
    {
      page: studioSection("node_services"),
      target: STUDIO_CONTENT,
      waitMs: 15000,
      title: "Node Services — the engine's code",
      body:
        "A <b>service</b> is a deployed container (Python, Node…) exposing functions that endpoint " +
        "nodes call — this is where the heavy lifting lives, e.g. the pyarrow processor that " +
        "applies your processing rules to every record, or database adapters. Editing a service " +
        "and <b>deploying</b> it ships a new build; logs are inspectable per service.",
    },
    {
      page: studioSection("folders"),
      target: STUDIO_CONTENT,
      waitMs: 15000,
      title: "Folders — file storage",
      body:
        "Project-scoped file storage the flows can read and write — staging areas, generated " +
        "artifacts, uploads in transit. When a pipeline stages intermediate data, this is one of " +
        "the places it can live.",
    },
    {
      page: studioSection("history"),
      target: STUDIO_CONTENT,
      waitMs: 15000,
      title: "History — the engine is versioned too",
      body:
        "Every endpoint edit and service change is recorded — browse what changed, when, and by " +
        "whom, and read older revisions. Together with the recipe's branch history on the Cortex " +
        "side, the <b>whole system is auditable</b>: config and code.",
    },
    {
      page: studioSection("export"),
      target: STUDIO_CONTENT,
      waitMs: 15000,
      title: "Export — moving flows between projects",
      body:
        "Endpoints and services can be exported and imported across engine projects. This is how " +
        "shared capability travels: build once on the template, inherit everywhere — or hand a " +
        "bespoke flow from one project to another.",
    },
    {
      page: studioSection("environment"),
      target: STUDIO_CONTENT,
      waitMs: 15000,
      title: "Environment — per-project configuration",
      body:
        "Environment variables scoped to this engine project: database credentials, storage " +
        "buckets, the recipe-database connection the pipeline reads. When Cortex dispatches a run, " +
        "the engine injects these — so the same flow logic works per project without editing code.",
    },
    {
      page: studioSection("project/knowledge"),
      target: STUDIO_CONTENT,
      waitMs: 15000,
      title: "Project knowledge — the vault",
      body:
        "Each engine project carries a <b>knowledge vault</b> — a linked-note graph describing what " +
        "the project is, its architecture and conventions. Agents read it for orientation before " +
        "touching anything (the assistant's engine persona starts here), and write back what they " +
        "learn. Institutional memory, next to the code it describes.",
    },
    {
      title: "How Cortex drives the engine",
      body:
        "When you press <b>Run</b> (or ask the assistant to), Cortex reads your pipeline graph at a " +
        "specific recipe version and dispatches each transformation to its engine endpoint — " +
        "<code>cortex.strategy.&lt;kind&gt;.apply</code> — feeding each step's output into the " +
        "next, with the project's environment injected. Adding a brand-new transformation " +
        "<i>kind</i> means authoring one new <code>apply</code> endpoint here; wiring existing " +
        "kinds into pipelines never touches the engine at all.",
    },
    {
      target: ['[data-tour="chat-launcher-engine"]', '[data-tour="chat-launcher-orchestrator"]'],
      placement: "left",
      title: "The engine assistant",
      body:
        "Engine work is chat-drivable too: the <b>engine persona</b> has direct access to this " +
        "workspace's tools (create/edit endpoints, deploy services, read logs, search the vault). " +
        "When you add a transformation whose kind doesn't exist yet, the wizard hands off to this " +
        "chat to author the <code>apply</code> endpoint for you.",
    },
    {
      title: "That's the engine",
      body:
        "<b>Recipe (Cortex) → dispatch → endpoints (flows) → services (code) → your targets & " +
        "apps</b>, with environment per project, full edit history, a knowledge vault, and a chat " +
        "persona that can build engine pieces on demand.<br/><br/>Replay this any time via " +
        "<b>Tutorial</b> in the sidebar — or take “What is Cortex?” for the governance-side story.",
    },
  ],
};
